/**
 * Compliance Report Agency Presets
 *
 * Recipient presets for the "Send Report to Agency" modal (FAA + State compliance reports).
 *
 * This is a frontend constants file, NOT a database table. The field names are deliberately
 * identical to the parked spec's `adn_report_agencies` columns so that this file can become
 * seed data verbatim if that table is ever built.
 *
 * Reference: /agent-os/specs/2026-08-03-report-agency-email-stopgap/spec.md § 5
 *
 * NOTE: the FAA recipient is supplied by `environment.faaReportEmail` rather than being
 * hardcoded here — real address in prod only, blank in local/stg. If this file is ever
 * promoted to `adn_report_agencies` seed data, that value must come from Hub config, not
 * from a literal in the migration.
 */

import { environment } from '../../../environments/environment';

// ============================================
// TYPES
// ============================================

/**
 * Which report kind an agency preset applies to.
 * Mirrors the shared email modal's `reportKind` input.
 */
export type ReportAgencyKind = 'faa' | 'state';

/**
 * A single recipient preset.
 *
 * Field names map 1:1 to the parked spec's `adn_report_agencies` columns:
 * `id`, `kind`, `jurisdiction`, `name`, `email`, `description`, `isDefault`, `sortOrder`.
 */
export interface ReportAgency {
  /** Stable identifier. Becomes the row's primary key if this file becomes seed data. */
  readonly id: string;
  /** `'faa'` presets apply to FAA reports; `'state'` presets apply to state reports. */
  readonly kind: ReportAgencyKind;
  /** `'US'` for federal entries; a 2-letter state code (e.g. `'AR'`) for state entries. */
  readonly jurisdiction: string;
  /** Display name shown in the recipient picker. */
  readonly name: string;
  /**
   * Recipient address.
   *
   * ⚠️ An empty string means "not yet confirmed" — see {@link isAgencyEmailPending}.
   * Never render an empty address as if it were a real one.
   */
  readonly email: string;
  /** Short helper copy rendered under the option row. */
  readonly description: string;
  /** Preselected for its `kind` when no better match exists. */
  readonly isDefault: boolean;
  /** Ascending display order within a `kind`. */
  readonly sortOrder: number;
}

// ============================================
// PRESETS
// ============================================

/**
 * Verified agency presets.
 *
 * ⚠️ Only verified addresses belong in this list. An unverified state simply has no entry —
 * the modal falls through to "Other recipient…", which always works. Guessing an agency address
 * is strictly worse than having none, because a wrong address on a compliance filing looks like
 * a completed submission that silently went nowhere.
 */
export const REPORT_AGENCIES = [
  {
    id: 'faa-uas',
    kind: 'faa',
    jurisdiction: 'US',
    name: 'FAA (Federal Aviation Administration)',
    // ⚠️ ENVIRONMENT-DRIVEN — DO NOT HARDCODE AN ADDRESS HERE.
    //
    // Real value lives ONLY in environment.prod.ts (the FAA COA/exemption reporting mailbox).
    // environment.ts (local) and environment.stg.ts are deliberately BLANK, so a developer or
    // a staging tester can never one-click a real compliance report to a federal agency.
    //
    // Blank == "pending": isAgencyEmailPending() returns true, the modal shows an
    // "address pending" notice and reveals the free-text recipient input, and the flow stays
    // fully exercisable against your own address.
    email: environment.faaReportEmail,
    description:
      'FAA reporting mailbox for COA / exemption compliance submissions. Confirm this matches the recipient named in your operating exemption or Part 137 certificate conditions before sending.',
    isDefault: true,
    sortOrder: 1
  }

  // ---------------------------------------------------------------------------------------
  // State agencies go here as they are verified, one entry per 2-letter `jurisdiction`, e.g.:
  //
  //   {
  //     id: 'ar-plant-board',
  //     kind: 'state',
  //     jurisdiction: 'AR',
  //     name: 'Arkansas State Plant Board',
  //     email: 'verified-address@example.gov',
  //     description: 'Pesticide application reporting',
  //     isDefault: false,
  //     sortOrder: 10
  //   }
  //
  // ⚠️ Add an entry ONLY after the address has been verified against the agency's own
  // published filing instructions. No entry is the correct state for an unverified agency.
  // ---------------------------------------------------------------------------------------
] as const satisfies readonly ReportAgency[];

// ============================================
// HELPERS
// ============================================

/**
 * True when an agency's recipient address has not been confirmed yet.
 *
 * The preset is still selectable — the user reviews and completes the recipient in their own
 * mail client before sending — but the UI must surface that the address needs to be filled in
 * and must not present an empty string as a real address.
 */
export function isAgencyEmailPending(agency: ReportAgency | null | undefined): boolean {
  return !agency || agency.email.trim().length === 0;
}

/**
 * All presets for a report kind, in display order.
 *
 * @param agencies Defaults to {@link REPORT_AGENCIES}; overridable so the matching logic stays
 *                 testable before real state entries have been verified.
 */
export function listReportAgencies(
  kind: ReportAgencyKind,
  agencies: readonly ReportAgency[] = REPORT_AGENCIES
): ReportAgency[] {
  return agencies.filter((agency) => agency.kind === kind).sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Which preset should be selected when the modal opens.
 *
 * - FAA reports → the default FAA entry (falls back to the first FAA entry if none is flagged).
 * - State reports → the entry whose `jurisdiction` matches the report's 2-letter state code.
 * - No match → `null`, and the caller falls through to "Other recipient…", which always works.
 *
 * @param kind     The report kind being sent.
 * @param state    The report's 2-letter state code. Ignored for FAA reports.
 * @param agencies Defaults to {@link REPORT_AGENCIES}; overridable for tests.
 */
export function preselectReportAgency(
  kind: ReportAgencyKind,
  state?: string | null,
  agencies: readonly ReportAgency[] = REPORT_AGENCIES
): ReportAgency | null {
  const candidates = listReportAgencies(kind, agencies);
  if (candidates.length === 0) {
    return null;
  }

  if (kind === 'faa') {
    return candidates.find((agency) => agency.isDefault) ?? candidates[0];
  }

  const jurisdiction = (state ?? '').trim().toUpperCase();
  if (!jurisdiction) {
    return null;
  }

  const matches = candidates.filter((agency) => agency.jurisdiction.toUpperCase() === jurisdiction);
  if (matches.length === 0) {
    return null;
  }

  return matches.find((agency) => agency.isDefault) ?? matches[0];
}
